import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo, cardUrl } from "../lib/ui";
import { AsOf } from "../components/AsOf";
import { Countdown } from "../components/Countdown";
import { FixtureList } from "../components/FixtureList";

const utc = (value) => (value ? (value.endsWith("Z") ? value : `${value}Z`) : "");

export default function HarambeeFixtures() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Harambee Stars fixtures and results",
    description:
      "Kenya's next fixtures and most recent results, refreshed daily from an open football database, with a live countdown to the next kick-off beside the FIFA ranking.",
    image: cardUrl("fixtures"),
  });

  useEffect(() => {
    api.get("/harambee-fixtures")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the fixture feed. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="fixtures-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="fixtures-loading" />;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="harambee-fixtures-page">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">The national game</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.title}</h1>
          <p className="body-copy mt-7 max-w-3xl">{d.standfirst}</p>
          <AsOf
            testId="fixtures-source-stamp"
            kind={d.source.kind}
            asOf={d.read_at}
            publisher={d.source.publisher}
            url={d.source.url}
          />
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-[1.2fr_1fr]">
          <div className="border-l-2 border-maasai bg-[#0b0b0b] p-6 md:p-8" data-testid="fixtures-next">
            <p className="overline text-ochre">Next up</p>
            {d.next ? (
              <>
                <p className="mt-4 font-display text-2xl font-bold uppercase leading-tight text-white md:text-4xl">
                  {d.next.home} <span className="text-zinc-600">v</span> {d.next.away}
                </p>
                <p className="mono mt-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-500">
                  {d.next.competition}
                  {d.next.venue ? ` · ${d.next.venue}` : ""}
                </p>
                <div className="mt-7">
                  <Countdown target={utc(d.next.kick_off)} label="Until kick-off" testId="fixtures-countdown" />
                </div>
              </>
            ) : (
              <p className="mt-4 text-sm text-zinc-500">No confirmed fixture in the feed yet.</p>
            )}
          </div>

          <div className="grid gap-px bg-zinc-800 sm:grid-cols-2" data-testid="fixtures-form">
            {[
              ["Played", d.form.played, `most recent ${d.form.played === 1 ? "match" : "matches"} in the feed`],
              ["Won", d.form.won, "of those matches"],
              ["Drawn", d.form.drawn, "of those matches"],
              ["Lost", d.form.lost, "of those matches"],
            ].map(([label, value, note], i) => (
              <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`fixtures-form-${i}`}>
                <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
                <p className="mt-4 font-display text-3xl font-bold leading-none text-white md:text-4xl">{value}</p>
                <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
              </div>
            ))}
          </div>
        </div>

        <section className="mt-12">
          <h2 className="display-3 border-b border-white/15 pb-4">Fixtures</h2>
          <div className="mt-6">
            <FixtureList matches={d.upcoming} prefix="fixtures-upcoming" empty="No confirmed fixtures in the feed yet." />
          </div>
        </section>

        <section className="mt-12">
          <h2 className="display-3 border-b border-white/15 pb-4">Results</h2>
          <div className="mt-6">
            <FixtureList matches={d.results} prefix="fixtures-results" empty="No results in the feed yet." />
          </div>
        </section>

        <div className="mt-10 border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="fixtures-how">
          <p className="overline text-ochre">How this feed works</p>
          <p className="mt-4 max-w-4xl whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.how_it_works}</p>
        </div>

        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/ranking-ambition" className="btn-primary" data-testid="fixtures-ambition-link">
            Where this could take the ranking <ArrowRight size={14} />
          </Link>
          <Link to="/fifa-ranking" className="btn-ghost" data-testid="fixtures-ranking-link">
            Kenya in the FIFA ranking
          </Link>
          <Link to="/fkf-feed" className="btn-ghost" data-testid="fixtures-feed-link">
            The FKF live feed
          </Link>
        </div>
      </div>
    </div>
  );
}
